import { Link } from "react-router-dom";
import { useState } from "react";
import { Gauge, Fuel, Cog, ChevronLeft, ChevronRight } from "lucide-react";
import { zar, mediaUrl } from "@/api";

const SERVICE_LABELS = {
  "rent-to-own": "Rent-to-Own",
  rental: "Rental",
  sale: "Sale",
};

const STATUS_LABELS = {
  sold: "Sold",
  reserved: "Reserved",
  "on-rent-to-own": "On Rent-to-Own",
};

export default function VehicleCard({ v, index = 0 }) {
  const photos = [v.image, ...(v.images || [])].filter(Boolean);
  const [photoIdx, setPhotoIdx] = useState(0);

  return (
    <div
      data-testid={`vehicle-card-${v.slug}`}
      className="group bg-[#122659] border border-sky-300/20 rounded-xl overflow-hidden shadow-xl hover:border-[#F57C00]/60 transition-colors duration-300"
    >
        <div className="relative h-52 overflow-hidden">
        <img
          src={mediaUrl(photos[photoIdx])}
          alt={`${v.make} ${v.model}`}
          loading={index > 2 ? "lazy" : "eager"}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        {photos.length > 1 && (
          <>
            <button
              data-testid={`photo-prev-${v.slug}`}
              onClick={() => setPhotoIdx((photoIdx - 1 + photos.length) % photos.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0B1F4E]/80 backdrop-blur text-white flex items-center justify-center hover:bg-[#F57C00] transition-colors z-10"
              aria-label="Previous photo"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid={`photo-next-${v.slug}`}
              onClick={() => setPhotoIdx((photoIdx + 1) % photos.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#0B1F4E]/80 backdrop-blur text-white flex items-center justify-center hover:bg-[#F57C00] transition-colors z-10"
              aria-label="Next photo"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid={`photo-dots-${v.slug}`}>
              {photos.map((_, i) => (
                <span key={i} className={`w-2 h-2 rounded-full transition-colors ${i === photoIdx ? "bg-[#F57C00]" : "bg-white/40"}`} />
              ))}
            </div>
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#122659] via-transparent to-transparent" />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="bg-[#F57C00] text-white text-xs font-bold px-3 py-1 rounded-full">
            from R{v.day_rate}/day
          </span>
          {v.status && v.status !== "available" && (
            <span
              data-testid={`vehicle-status-${v.slug}`}
              className="bg-[#0B1F4E]/90 backdrop-blur text-sky-300 text-xs font-bold px-3 py-1 rounded-full border border-sky-300/40"
            >
              {STATUS_LABELS[v.status] || v.status}
            </span>
          )}
        </div>
      </div>
      <div className="p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-sky-300 font-bold mb-1">{v.year} · {v.make}</p>
        <h3 className="font-display text-xl font-bold text-white mb-2">{v.model}</h3>
        <p className="text-sm text-slate-400 mb-4">{v.blurb}</p>
        <div className="flex flex-wrap gap-4 text-xs text-slate-400 mb-4">
          <span className="flex items-center gap-1.5"><Gauge size={14} className="text-sky-300" />{Number(v.mileage_km).toLocaleString()} km</span>
          <span className="flex items-center gap-1.5"><Cog size={14} className="text-sky-300" />{v.transmission}</span>
          <span className="flex items-center gap-1.5"><Fuel size={14} className="text-sky-300" />{v.fuel}</span>
        </div>
        <div className="flex flex-wrap gap-2 mb-5">
          {v.services.map((s) => (
            <span key={s} className="text-[10px] uppercase tracking-wider font-bold text-sky-300 border border-sky-300/30 rounded-full px-2.5 py-1">
              {SERVICE_LABELS[s] || s}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="font-display text-2xl font-black text-white">{zar(v.price)}</span>
          {v.status === "sold" ? (
            <span
              data-testid={`vehicle-sold-badge-${v.slug}`}
              className="text-slate-500 text-sm font-bold px-5 py-2.5 rounded-md border border-slate-600 cursor-not-allowed"
            >
              Sold
            </span>
          ) : (
            <div className="flex gap-2">
              {(v.services || []).includes("rental") && (
                <Link
                  to={`/rental?vehicle=${v.slug}`}
                  data-testid={`vehicle-rent-btn-${v.slug}`}
                  className="border-2 border-[#87CEEB] text-[#87CEEB] hover:bg-[#87CEEB] hover:text-[#0B1F4E] text-sm font-bold px-4 py-2.5 rounded-md transition-colors duration-300"
                >
                  Rent
                </Link>
              )}
              <Link
                to={`/apply?vehicle=${v.slug}`}
                data-testid={`vehicle-apply-btn-${v.slug}`}
                className="bg-[#F57C00] hover:bg-[#E67300] text-white text-sm font-bold px-5 py-2.5 rounded-md transition-colors duration-300"
              >
                Apply
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
